import React, { useState, useMemo } from "react";
import axios from "axios";
import { Box, CircularProgress, Typography, TextField } from "@mui/material";
import {
  maintenanceTableColumns,
  type MaintenanceStatus,
} from "../../../data/FormColumns/Fleet/maintenanceTableColumns";
import { apiVariable } from "../../../api/apiVariable";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import "./MaintenanceTable.css";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const fetchMaintenanceData = async (token: string | null | undefined) => {
  const { data } = await axios.get(
    `${apiVariable}/getCriticalMaintenanceOperations`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

interface MaintenanceTableProps {
  allowedOperations?: string[];
  allowedStatuses?: MaintenanceStatus[];
}

const MaintenanceTable: React.FC<MaintenanceTableProps> = ({
  allowedOperations,
  allowedStatuses,
}) => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: criticalMaintenances,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["criticalMaintenances"],
    queryFn: () => fetchMaintenanceData(token),
  });

  const onRowClick = (params: any, event: React.MouseEvent) => {
    const assetType: string = params.row.assetType;
    const assetId: string = params.row.assetId;
    const operationId: string = params.row.id;

    const absolutePath = `/fleet/assets/${assetType}/${assetId}/maintenance/${operationId}`;

    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      event.stopPropagation();
      window.open(absolutePath, "_blank");
    } else {
      navigate(absolutePath);
    }
  };

  const filteredMaintenances = useMemo(() => {
    if (!criticalMaintenances) return [];
    const search = searchTerm.toLowerCase();
    const allowedOps = allowedOperations?.map((op) => op.toLowerCase().trim());
    const allowedStatusesLower = allowedStatuses?.map((status) =>
      status.toLowerCase().trim(),
    );

    return criticalMaintenances.filter((maintenance: any) => {
      const matchesSearch = maintenance.unitNumber
        ?.toString()
        .toLowerCase()
        .includes(search);
      if (!matchesSearch) return false;

      if (allowedOps && maintenance.operationPerformed) {
        const op = (maintenance.operationPerformed as string)
          .toLowerCase()
          .trim();
        if (!allowedOps.includes(op)) {
          return false;
        }
      } else if (allowedOps) {
        return false;
      }

      if (allowedStatusesLower) {
        const status = maintenance.status
          ? String(maintenance.status).toLowerCase().trim()
          : "";
        if (!allowedStatusesLower.includes(status)) {
          return false;
        }
      }

      return true;
    });
  }, [criticalMaintenances, searchTerm, allowedOperations, allowedStatuses]);

  return (
    <Box className="maintenance-table" sx={{ height: 900 }}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          marginBottom: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", sm: 400 },
            minWidth: 0,
            alignItems: "flex-end",
            position: "relative",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by Unit Number"
            sx={{ maxWidth: "100%", width: 400 }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {(error as any).message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="maintenance"
          rows={filteredMaintenances}
          columns={maintenanceTableColumns}
          onRowClick={onRowClick}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default MaintenanceTable;
